import { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, File, X, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from 'axios';
import { pdfjs } from 'react-pdf';
import { useSettings } from '../shared/hooks/useSettings';
import {
  CompreAgoraAllowedProduct,
  CompreAgoraAnalyticRow,
  CompreAgoraBundleResult,
  CompreAgoraExistingSupplier,
  CompreAgoraParserOptions,
  CompreAgoraRoleSummary,
  CompreAgoraSupplierImport,
  detectCompreAgoraBundle,
  parseCompreAgoraBundle,
} from './compreAgoraBundleParser';
import { CompreAgoraPreviewTable } from './CompreAgoraPreviewTable';

const EXPECTED_COLUMNS = ['EAN', 'Produto', 'CNPJ Loja', 'Saldo Inicial', 'Entrada', 'Saida', 'Saldo final', 'Estoque atual'];
const CELTA_CNPJ_BY_FILE_SUFFIX: Record<string, string> = {
  L1: '56148687000161',
  L2: '07583017000168',
};

const PRODUCT_EAN_DEPARA: Array<[string, string]> = [
  ['7891150064317', 'Sabão em Pó Omo Lavagem Perfeita 800g'],
  ['7891150064331', 'Sabão Em Pó Omo Lavagem Perfeita 1,6Kg'],
  ['7891150020689', 'Sabão Líquido Omo Lavagem Perfeita 3 L'],
  ['7506306241183', 'Desodorante Aerossol Dove Original 150ml'],
  ['7791293032436', 'Desodorante Rexona Antitranspirante Feminino Aerosol Powder Dry 72 horas 150mL'],
  ['7898422746759', 'Sabonete em Barra Dove Original 90g'],
  ['7891150037397', 'SEDA SH CERAMIDAS 12X325ML'],
  ['7891150037519', 'SEDA SH LISO PERFEITO 12X325ML'],
  ['7898422746216', 'CLEAR SH 2EM1 DUAL EFFECT 12X200ML'],
  ['7894000030470', "Hellmann's Maionese Tradicional 200g"],
  ['7894000050034', "Maionese Regular Hellmann's 500g"],
  ['7891150080546', "Maionese Hellmann's NBA Supreme 330g"],
  ['7506306241183', 'DES.DOVE AERO 150ML ORIGINAL (UN 1.000)'],
  ['7791293032436', 'DES.REXONA AERO 150ML POWDER DRY (UN 1.000)'],
  ['7894000030470', 'MAIONESE HELLMANNS 200G SACHET (UN 1.000)'],
  ['7891150080546', 'MAIONESE HELLMANNS 330G SUPREME (UN 1.000)'],
  ['7894000050034', 'MAIONESE HELLMANNS 500G TRADICIONAL (UN 1.000)'],
  ['7891150064331', 'DET.PO OMO 1,6KG LAVAGEM PERFEITA  (UN 1.000)'],
  ['7891150020689', 'LAVA ROUPA OMO LIQUIDO 3L PODER ACELERADOR (UN 1.000)'],
  ['7891150064317', 'DET.PO OMO 800G LAVAGEM PERFEITA'],
  ['7898422746759', 'SAB.DOVE 90G ORIGINAL (UN 1.000)'],
  ['7898422746216', 'SH.CLEAR 200ML MEN LIMPEZA DIARIA 2 EM 1 (UN 1.000)'],
  ['7891150037397', 'SH.SEDA 325ML CERAMIDAS (UN 1.000)'],
  ['7891150037519', 'SH.SEDA 325ML LISO PERFEITO  (UN 1.000)'],
];

pdfjs.GlobalWorkerOptions.workerSrc =
  pdfjs.GlobalWorkerOptions.workerSrc || `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

interface ParsedRow {
  ean: string;
  produto: string;
  cnpjLoja: string;
  fornecedor?: string;
  saldoInicial: number;
  entrada: number;
  saida: number;
  saldoFinal: number;
  estoqueAtual: number;
}

interface InvalidItem {
  index: number;
  item: Record<string, unknown>;
  errors: string[];
}

interface UploadResponseData {
  total_received: number;
  total_valid: number;
  total_invalid: number;
  inserted: number;
  updated: number;
  logged: number;
  invalid_items: InvalidItem[];
}

interface ImportResult {
  fileName: string;
  fileCount: number;
  rowCount: number;
  analyticRowCount?: number;
  timestamp: string;
  rows: ParsedRow[];
  apiData: UploadResponseData;
}

type Step = 'upload' | 'preview' | 'done';
type ParseResult = { rows: ParsedRow[]; errors: string[] };
type SheetRow = unknown[];

function isValidImportFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  return `${(bytes / 1024).toFixed(2)} KB`;
}

function getCellText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function normalizeHeader(header: string): string {
  return getCellText(header).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function stripUnitSuffix(product: string): string {
  return product.replace(/\s*\(UN\s+[\d.,]+\)\s*$/i, '').trim();
}

function getProductKeys(product: string): string[] {
  const keys = [product, stripUnitSuffix(product)].map(normalizeHeader).filter(Boolean);
  return Array.from(new Set(keys));
}

function matchColumn(header: string, expected: string): boolean {
  return normalizeHeader(header) === normalizeHeader(expected);
}

function findColumnIndex(headers: string[], expected: string): number {
  return headers.findIndex((h) => matchColumn(h, expected));
}

const PRODUCT_EAN_BY_NAME = PRODUCT_EAN_DEPARA.reduce((map, [ean, product]) => {
  for (const key of getProductKeys(product)) {
    map.set(key, ean);
  }
  return map;
}, new Map<string, string>());

function getProductFromFileName(fileName: string): string {
  const baseName = normalizeHeader(fileName.replace(/\.[^.]+$/, ''));
  const fileProductMap: Array<[string, string]> = [
    ['maionese200', 'MAIONESE HELLMANNS 200G SACHET (UN 1.000)'],
    ['maionese500', 'MAIONESE HELLMANNS 500G TRADICIONAL (UN 1.000)'],
    ['omo800', 'DET.PO OMO 800G LAVAGEM PERFEITA'],
    ['omo1,6', 'DET.PO OMO 1,6KG LAVAGEM PERFEITA  (UN 1.000)'],
    ['omo16', 'DET.PO OMO 1,6KG LAVAGEM PERFEITA  (UN 1.000)'],
  ];

  const match = fileProductMap.find(([fileKey]) => baseName.includes(normalizeHeader(fileKey)));
  return match?.[1] ?? '';
}

function isProductLikeCandidate(product: string): boolean {
  const normalizedProduct = normalizeHeader(product);
  if (!normalizedProduct) return false;

  const invalidCandidates = [
    'custo de',
    'reposicao total',
    'quantidade',
    'plu',
    'produto',
    'data do',
    'movimento',
    'data do movimento',
    'total',
    'justificativa',
    'entrada',
    'saida',
    'saldo inicial',
    'saldo final',
    'estoque atual',
  ];

  if (invalidCandidates.includes(normalizedProduct)) return false;
  return resolveEanForProduct(product) !== null;
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  let text = getCellText(value).replace(/[^\d,.-]/g, '');
  if (!text || !/\d/.test(text)) return null;

  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');

  if (lastComma !== -1 && lastDot !== -1) {
    text = lastComma > lastDot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '');
  } else if (lastComma !== -1) {
    text = text.replace(',', '.');
  }

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

function getCnpjFromFileName(fileName: string): string | null {
  const baseName = fileName.split(/[\\/]/).pop()?.replace(/\.[^.]+$/, '').toUpperCase() ?? '';
  const suffix = Object.keys(CELTA_CNPJ_BY_FILE_SUFFIX).find((key) => baseName.endsWith(key));
  return suffix ? CELTA_CNPJ_BY_FILE_SUFFIX[suffix] : null;
}

function resolveEanForProduct(product: string): string | null {
  const productKeys = getProductKeys(product);

  for (const key of productKeys) {
    const exactMatch = PRODUCT_EAN_BY_NAME.get(key);
    if (exactMatch) return exactMatch;
  }

  const [mainKey] = productKeys;
  if (!mainKey || mainKey.length < 5) return null;

  let fallbackEan: string | null = null;
  PRODUCT_EAN_BY_NAME.forEach((ean, knownProduct) => {
    if (fallbackEan) return;
    if (mainKey.includes(knownProduct) || knownProduct.includes(mainKey)) {
      fallbackEan = ean;
    }
  });

  return fallbackEan;
}

function isCeltaReport(rows: SheetRow[]): boolean {
  const firstRowsText = normalizeHeader(rows.slice(0, 12).flat().map(getCellText).join(' '));
  return firstRowsText.includes('relatorio de movimentacao de estoque') || firstRowsText.includes('celta business solutions');
}

function findReportProduct(rows: SheetRow[]): string {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const productColumnIndex = row.findIndex((cell) => matchColumn(getCellText(cell), 'Produto'));

    if (productColumnIndex === -1) continue;

    for (let j = i + 1; j < rows.length; j++) {
      const candidate = getCellText(rows[j]?.[productColumnIndex]);
      const normalizedCandidate = normalizeHeader(candidate);

      if (candidate && normalizedCandidate !== 'produto' && normalizedCandidate !== 'total' && isProductLikeCandidate(candidate)) {
        return candidate;
      }
    }
  }

  const text = rows.flat().map(getCellText).join(' ');
  const productMatch = text.match(/Produto:\s*(.*?)\s+Embalagem:/i);
  if (productMatch?.[1]) {
    return productMatch[1].trim();
  }

  const normalizedText = normalizeHeader(text);
  for (const [, product] of PRODUCT_EAN_DEPARA) {
    const [productKey] = getProductKeys(product);
    if (productKey && normalizedText.includes(productKey)) {
      return product;
    }
  }

  return '';
}

function findTotalsFromMovementRows(rows: SheetRow[]) {
  let entrada = 0;
  let saida = 0;

  for (const row of rows) {
    const normalizedRow = normalizeHeader(row.map(getCellText).join(' '));
    if (!normalizedRow.includes('total')) continue;

    const numbers = row.map(parseNumber).filter((value): value is number => value !== null);
    if (numbers.length === 0) continue;

    const quantity = numbers[0];
    if (quantity > 0) {
      entrada += quantity;
    } else if (quantity < 0) {
      saida += Math.abs(quantity);
    }
  }

  return { entrada, saida };
}

function findNumberAfterLabel(rows: SheetRow[], label: string): number | null {
  const normalizedLabel = normalizeHeader(label);

  for (const row of rows) {
    for (let i = 0; i < row.length; i++) {
      if (normalizeHeader(getCellText(row[i])) !== normalizedLabel) continue;

      for (let j = i + 1; j < row.length; j++) {
        const value = parseNumber(row[j]);
        if (value !== null) return value;
      }

      for (let j = 0; j < row.length; j++) {
        if (j === i) continue;
        const value = parseNumber(row[j]);
        if (value !== null) return value;
      }
    }
  }

  return null;
}

function parseCeltaReportRows(rows: SheetRow[], fileName: string): ParseResult {
  const errors: string[] = [];
  const product = getProductFromFileName(fileName) || findReportProduct(rows);
  const ean = product ? resolveEanForProduct(product) : null;
  const cnpjLoja = getCnpjFromFileName(fileName);
  const movementTotals = findTotalsFromMovementRows(rows);

  const saldoInicial = findNumberAfterLabel(rows, 'Saldo inicial');
  const entrada = findNumberAfterLabel(rows, 'Total das entradas') ?? movementTotals.entrada;
  const saida = findNumberAfterLabel(rows, 'Total de saídas') ?? findNumberAfterLabel(rows, 'Total de saidas') ?? movementTotals.saida;
  const saldoFinal = findNumberAfterLabel(rows, 'Saldo final');
  const estoqueAtual = findNumberAfterLabel(rows, 'Estoque atual');

  if (!product) errors.push('Produto nao encontrado no relatorio Celta.');
  if (!ean && product) errors.push(`EAN nao encontrado no depara para o produto "${product}".`);
  if (!cnpjLoja) errors.push('CNPJ da loja nao identificado pelo final do nome do arquivo. Use arquivos terminando com L1 ou L2.');
  if (saldoInicial === null) errors.push('Campo "Saldo inicial" nao encontrado no relatorio.');
  if (entrada === null) errors.push('Campo "Total das entradas" nao encontrado no relatorio.');
  if (saida === null) errors.push('Campo "Total de saidas" nao encontrado no relatorio.');
  if (saldoFinal === null) errors.push('Campo "Saldo final" nao encontrado no relatorio.');
  if (estoqueAtual === null) errors.push('Campo "Estoque atual" nao encontrado no relatorio.');

  if (errors.length > 0) {
    return { rows: [], errors };
  }

  return {
    rows: [{
      ean: ean as string,
      produto: product,
      cnpjLoja: cnpjLoja as string,
      saldoInicial: saldoInicial as number,
      entrada: Math.abs(entrada as number),
      saida: Math.abs(saida as number),
      saldoFinal: saldoFinal as number,
      estoqueAtual: estoqueAtual as number,
    }],
    errors,
  };
}

function parseExpectedColumnRows(jsonData: SheetRow[]): ParseResult {
  if (jsonData.length < 2) {
    return { rows: [], errors: ['O arquivo esta vazio ou nao possui dados alem do cabecalho.'] };
  }

  const headers = jsonData[0].map(getCellText);
  const errors: string[] = [];

  const colIdx: Record<string, number> = {};
  for (const col of EXPECTED_COLUMNS) {
    const idx = findColumnIndex(headers, col);
    if (idx === -1) {
      errors.push(`Coluna "${col}" nao encontrada no arquivo.`);
    }
    colIdx[col] = idx;
  }

  if (errors.length > 0) {
    return { rows: [], errors };
  }

  const rows: ParsedRow[] = [];

  for (let i = 1; i < jsonData.length; i++) {
    const row = jsonData[i];
    if (!row || row.length === 0) continue;

    const ean = getCellText(row[colIdx['EAN']]);
    if (!ean) continue;

    rows.push({
      ean,
      produto: getCellText(row[colIdx['Produto']]),
      cnpjLoja: getCellText(row[colIdx['CNPJ Loja']]),
      saldoInicial: parseNumber(row[colIdx['Saldo Inicial']]) ?? 0,
      entrada: parseNumber(row[colIdx['Entrada']]) ?? 0,
      saida: parseNumber(row[colIdx['Saida']]) ?? 0,
      saldoFinal: parseNumber(row[colIdx['Saldo final']]) ?? 0,
      estoqueAtual: parseNumber(row[colIdx['Estoque atual']]) ?? 0,
    });
  }

  if (rows.length === 0) {
    errors.push('Nenhuma linha de dados valida encontrada no arquivo.');
  }

  return { rows, errors };
}

function parseXlsxRows(data: ArrayBuffer, fileName: string): ParseResult {
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  const jsonData = XLSX.utils.sheet_to_json<SheetRow>(worksheet, { header: 1, defval: '', raw: false });

  if (isCeltaReport(jsonData) || getProductFromFileName(fileName)) {
    return parseCeltaReportRows(jsonData, fileName);
  }

  return parseExpectedColumnRows(jsonData);
}

function isPdfLikeXls(data: ArrayBuffer): boolean {
  return new TextDecoder('ascii').decode(data.slice(0, 5)) === '%PDF-';
}

async function extractPdfLikeRows(data: ArrayBuffer): Promise<SheetRow[]> {
  const document = await pdfjs.getDocument({ data: data.slice(0) }).promise;
  const rows: SheetRow[] = [];

  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber++) {
    const page = await document.getPage(pageNumber);
    const content = await page.getTextContent();
    const items = content.items
      .map((item) => {
        const textItem = item as { str?: string; transform?: number[] };
        return {
          text: getCellText(textItem.str),
          x: textItem.transform?.[4] ?? 0,
          y: textItem.transform?.[5] ?? 0,
        };
      })
      .filter((item) => item.text);

    const groupedRows = items.reduce<Array<{ y: number; items: typeof items }>>((groups, item) => {
      const group = groups.find((current) => Math.abs(current.y - item.y) <= 2);
      if (group) {
        group.items.push(item);
      } else {
        groups.push({ y: item.y, items: [item] });
      }
      return groups;
    }, []);

    groupedRows
      .sort((a, b) => b.y - a.y)
      .forEach((group) => {
        rows.push(group.items.sort((a, b) => a.x - b.x).map((item) => item.text));
      });
  }

  return rows;
}

async function parseImportFile(data: ArrayBuffer, fileName: string): Promise<ParseResult> {
  try {
    if (isPdfLikeXls(data)) {
      const rows = await extractPdfLikeRows(data);
      return parseCeltaReportRows(rows, fileName);
    }

    return parseXlsxRows(data, fileName);
  } catch {
    return { rows: [], errors: ['Erro ao ler o arquivo. Verifique se o formato esta correto.'] };
  }
}

interface AllowedProductsApiResponse {
  success: boolean;
  data?: CompreAgoraAllowedProduct[];
  error?: string;
}

interface SuppliersApiResponse {
  success: boolean;
  data?: CompreAgoraExistingSupplier[];
  error?: string;
}

interface BundleWorkerResponse {
  result?: CompreAgoraBundleResult;
  error?: string;
}

function parseBundleInWorker(
  files: Array<{name: string; data: ArrayBuffer}>,
  options: CompreAgoraParserOptions
): Promise<CompreAgoraBundleResult> {
  if (typeof Worker === 'undefined') {
    return Promise.resolve(parseCompreAgoraBundle(files, options));
  }

  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./compreAgoraBundle.worker.ts', import.meta.url));
    const finish = () => worker.terminate();

    worker.onmessage = (event: MessageEvent<BundleWorkerResponse>) => {
      finish();
      if (event.data.error) {
        reject(new Error(event.data.error));
        return;
      }
      if (!event.data.result) {
        reject(new Error('O processamento das planilhas não retornou resultado.'));
        return;
      }
      resolve(event.data.result);
    };
    worker.onerror = (event) => {
      finish();
      reject(new Error(event.message || 'Erro ao processar as planilhas.'));
    };

    worker.postMessage(
      {files, options},
      files.map((file) => file.data)
    );
  });
}

export function ImportPage() {
  const { settings } = useSettings();
  const [step, setStep] = useState<Step>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [analyticRows, setAnalyticRows] = useState<CompreAgoraAnalyticRow[]>([]);
  const [roleSummaries, setRoleSummaries] = useState<CompreAgoraRoleSummary[]>([]);
  const [supplierImports, setSupplierImports] = useState<CompreAgoraSupplierImport[]>([]);
  const [allowedProductCount, setAllowedProductCount] = useState(0);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(isValidImportFile);
    if (files.length > 0) {
      setUploadedFiles(files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter(isValidImportFile);
    if (files.length > 0) {
      setUploadedFiles(files);
    }
  };

  const handleProcessFile = async () => {
    if (uploadedFiles.length === 0) return;

    setIsProcessing(true);
    setParseErrors([]);
    setAllowedProductCount(0);
    setSupplierImports([]);

    try {
      const sourceFiles: Array<{name: string; data: ArrayBuffer}> = [];
      for (const file of uploadedFiles) {
        sourceFiles.push({name: file.name, data: await file.arrayBuffer()});
      }
      const bundleDetection = detectCompreAgoraBundle(sourceFiles);
      if (bundleDetection.matched) {
        const [productsResponse, suppliersResponse] = await Promise.all([
          api.get<AllowedProductsApiResponse>('/stock-intelligence/allowed-products'),
          api.get<SuppliersApiResponse>('/sellers/stock-intelligence/suppliers'),
        ]);
        if (!productsResponse.data.success || !productsResponse.data.data?.length) {
          throw new Error(
            productsResponse.data.error ||
              'Não foi possível carregar os EANs Unilever/Kenvue no Databricks.'
          );
        }
        if (!suppliersResponse.data.success) {
          throw new Error(
            suppliersResponse.data.error || 'Não foi possível carregar os fornecedores cadastrados.'
          );
        }

        const bundleResult = await parseBundleInWorker(sourceFiles, {
          leadTimeDays: settings.leadTime,
          targetCoverageDays: settings.diasCoberturaAlvo,
          allowedProducts: productsResponse.data.data,
          existingSuppliers: suppliersResponse.data.data ?? [],
        });

        if (bundleResult.errors.length > 0) {
          setParseErrors(bundleResult.errors);
          return;
        }
        if (bundleResult.analyticRows.length === 0 || bundleResult.importRows.length === 0) {
          setParseErrors(['Nenhuma linha válida encontrada no pacote CompreAgora.']);
          return;
        }

        setAnalyticRows(bundleResult.analyticRows);
        setRoleSummaries(bundleResult.roleSummaries);
        setSupplierImports(bundleResult.suppliers);
        setAllowedProductCount(bundleResult.allowedProductCount);
        setParsedRows(bundleResult.importRows);
        setStep('preview');
        return;
      }

      const allRows: ParsedRow[] = [];
      const allErrors: string[] = [];

      for (const sourceFile of sourceFiles) {
        const { rows, errors } = await parseImportFile(sourceFile.data, sourceFile.name);

        if (errors.length > 0) {
          allErrors.push(...errors.map((error) => `${sourceFile.name}: ${error}`));
          continue;
        }

        allRows.push(...rows);
      }

      if (allErrors.length > 0) {
        setParseErrors(allErrors);
        return;
      }

      if (allRows.length === 0) {
        setParseErrors(['Nenhuma linha de dados valida encontrada nos arquivos selecionados.']);
        return;
      }

      setAnalyticRows([]);
      setRoleSummaries([]);
      setParsedRows(allRows);
      setStep('preview');
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Erro ao ler os arquivos. Verifique se os formatos estão corretos.';
      setParseErrors([message]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmImport = async () => {
    if (parsedRows.length === 0 || uploadedFiles.length === 0) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const items = parsedRows.map((row) => ({
        ean: row.ean,
        produto: row.produto,
        cnpj: row.cnpjLoja,
        fornecedor: row.fornecedor,
        saldo_inicial: row.saldoInicial,
        entrada: row.entrada,
        saida: row.saida,
        saldo_final: row.saldoFinal,
        estoque_atual: row.estoqueAtual,
      }));

      const suppliers = supplierImports.map((supplier) => ({
        cnpj: supplier.cnpjLoja,
        fornecedor: supplier.fornecedor,
        lead_time: supplier.leadTimeDays,
        lead_time_source: supplier.leadTimeSource,
      }));

      const response = await api.post<{
        success: boolean;
        message?: string;
        error?: string;
        data: UploadResponseData;
      }>('/stock-intelligence/upload', {items, suppliers});

      if (!response.data.success) {
        throw new Error(
          response.data.message || response.data.error || 'Erro ao processar upload'
        );
      }

      setImportResult({
        fileName: uploadedFiles.map((file) => file.name).join(', '),
        fileCount: uploadedFiles.length,
        rowCount: parsedRows.length,
        analyticRowCount: analyticRows.length || undefined,
        timestamp: new Date().toLocaleString('pt-BR'),
        rows: parsedRows,
        apiData: response.data.data,
      });
      setStep('done');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao enviar dados para o servidor';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setUploadedFiles([]);
    setAnalyticRows([]);
    setParsedRows([]);
    setRoleSummaries([]);
    setSupplierImports([]);
    setAllowedProductCount(0);
    setParseErrors([]);
    setImportResult(null);
    setSaveError(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    const sampleData = [
      EXPECTED_COLUMNS,
      ['7506306241183', 'DES.DOVE AERO 150ML', '07583017000168', '50', '20', '36', '34', '34'],
      ['7791293032436', 'REXONA AERO 150ML', '07583017000168', '30', '15', '25', '20', '20'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    const colWidths = EXPECTED_COLUMNS.map((col) => ({ wch: Math.max(col.length + 4, 18) }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'template_importacao_estoque.xlsx');
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Importar Dados</h1>
        <p className="text-gray-600 mt-2">Faça upload de planilhas Excel com os dados de giro do estoque</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="font-semibold text-gray-900">Formato Aceito</h3>
          </div>
          <p className="text-sm text-gray-600">Excel (.xlsx, .xls)</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-700" />
            </div>
            <h3 className="font-semibold text-gray-900">Layouts Aceitos</h3>
          </div>
          <p className="text-sm text-gray-600">Modelo padrão ou relatório Celta</p>
          <p className="mt-2 text-sm font-medium text-green-700">
            Pacote CompreAgora: Estoque + Entradas + OTIF
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Download className="w-5 h-5 text-teal-700" />
            </div>
            <h3 className="font-semibold text-gray-900">Template Disponível</h3>
          </div>
          <button
            onClick={downloadTemplate}
            className="text-sm text-teal-600 hover:text-teal-800 font-medium"
          >
            Baixar modelo .xlsx
          </button>
        </div>
      </div>

      {step === 'upload' && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              isDragging
                ? 'border-teal-500 bg-teal-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <Upload className="w-12 h-12 text-gray-600" />
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {uploadedFiles.length > 0 ? 'Arquivos Selecionados' : 'Arraste e solte seus arquivos aqui'}
              </h3>

              {uploadedFiles.length > 0 ? (
                <div className="mb-4 w-full max-w-xl">
                  <div className="bg-gray-50 px-4 py-2 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        {uploadedFiles.length} arquivo(s)
                      </span>
                      <button
                        onClick={handleReset}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {uploadedFiles.map((file) => (
                        <div key={`${file.name}-${file.size}`} className="flex items-center space-x-2">
                          <File className="w-5 h-5 text-gray-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 truncate">{file.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Tamanho total: {formatFileSize(uploadedFiles.reduce((total, file) => total + file.size, 0))}
                  </p>
                  <label
                    htmlFor="file-upload"
                    className="inline-block text-sm text-teal-600 hover:text-teal-800 font-medium cursor-pointer mt-2"
                  >
                    Trocar arquivos
                  </label>
                </div>
              ) : (
                <p className="text-gray-600 mb-6">ou clique para selecionar</p>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />

              {uploadedFiles.length === 0 ? (
                <label
                  htmlFor="file-upload"
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer font-medium"
                >
                  Selecionar Arquivos
                </label>
              ) : (
                <button
                  onClick={handleProcessFile}
                  disabled={isProcessing}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <span className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Processando...</span>
                    </span>
                  ) : (
                    'Processar Arquivos'
                  )}
                </button>
              )}
              <p className="text-xs text-gray-500 mt-4">
                Formatos suportados: Excel (.xlsx, .xls). Até cerca de 200 mil linhas por arquivo.
              </p>
            </div>
          </div>

          {parseErrors.length > 0 && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Erros na validação</h4>
                  <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                    {parseErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-700 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Estrutura do Arquivo</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Sua planilha pode usar o modelo padrão abaixo ou o relatório Celta de movimentação de estoque. Nos relatórios Celta, o CNPJ vem do final do nome do arquivo: L1 ou L2.
                </p>
                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-100 p-3 text-sm text-blue-900">
                  <p className="font-semibold">Layout CompreAgora</p>
                  <p className="mt-1">
                    Se um dos anexos for reconhecido, selecione em conjunto pelo menos um arquivo de cada tipo:
                    Estoque (Anexo 1), Entradas (Anexo 2) e OTIF (Anexo 3). A identificação é feita pelos cabeçalhos, não pelo nome do arquivo.
                  </p>
                  <p className="mt-2 font-medium">
                    Apenas EANs ativos de Unilever ou Kenvue em 2026 entram na junção e nos cálculos.
                  </p>
                </div>
                <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-blue-100">
                      <tr>
                        {EXPECTED_COLUMNS.map((col) => (
                          <th key={col} className="px-3 py-2 text-xs font-semibold text-blue-900 text-left">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="text-xs text-blue-700">
                        <td className="px-3 py-2 font-mono">7506306241183</td>
                        <td className="px-3 py-2">DES.DOVE AERO 150ML</td>
                        <td className="px-3 py-2 font-mono">07583017000168</td>
                        <td className="px-3 py-2 text-right">50</td>
                        <td className="px-3 py-2 text-right">20</td>
                        <td className="px-3 py-2 text-right">36</td>
                        <td className="px-3 py-2 text-right">34</td>
                        <td className="px-3 py-2 text-right">34</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <FileSpreadsheet className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Preview dos Dados</h3>
                  <p className="text-sm text-gray-600">
                    {analyticRows.length > 0
                      ? `${analyticRows.length} linhas de tbl_raw; ${parsedRows.length} registros atuais prontos para importação em ${uploadedFiles.length} arquivo(s)`
                      : `${parsedRows.length} registros encontrados em ${uploadedFiles.length} arquivo(s)`}
                  </p>
                  {allowedProductCount > 0 && (
                    <p className="mt-1 text-xs font-medium text-teal-700">
                      Base Databricks: {allowedProductCount} EANs ativos de Unilever/Kenvue.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmImport}
                  disabled={isSaving}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Confirmar Importação</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {roleSummaries.length > 0 && (
              <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                {roleSummaries.map((summary) => (
                  <div key={summary.role} className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-semibold uppercase text-blue-700">
                      {summary.role === 'estoque'
                        ? 'Estoque (Anexo 1)'
                        : summary.role === 'entradas'
                        ? 'Entradas (Anexo 2)'
                        : 'OTIF (Anexo 3)'}
                    </p>
                    <p className="mt-1 text-lg font-bold text-blue-900">
                      {summary.eligibleRowCount} elegíveis de {summary.rowCount} linhas
                    </p>
                    <p className="truncate text-xs text-blue-700" title={summary.fileNames.join(', ')}>
                      {summary.fileNames.join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {supplierImports.length > 0 && (
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <span className="font-semibold">{supplierImports.length} fornecedores identificados.</span>{' '}
                Novos fornecedores terão o lead time cadastrado automaticamente; valores já existentes
                serão preservados.
              </div>
            )}

            {saveError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{saveError}</p>
              </div>
            )}

            {analyticRows.length > 0 ? (
              <CompreAgoraPreviewTable rows={analyticRows} />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">EAN</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Produto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">CNPJ Loja</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Saldo Inicial</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Entrada</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Saida</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Saldo Final</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Estoque Atual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {parsedRows.slice(0, 50).map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{row.ean}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.produto}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{row.cnpjLoja}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.saldoInicial}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.entrada}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.saida}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.saldoFinal}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{row.estoqueAtual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedRows.length > 50 && (
                <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600 border-t border-gray-200">
                  Mostrando 50 de {parsedRows.length} registros
                </div>
              )}
              </div>
            )}
          </div>
        </div>
      )}

      {step === 'done' && importResult && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Importação Concluída</h3>
                  <p className="text-sm text-gray-600">
                    {importResult.analyticRowCount
                      ? `${importResult.rowCount} registros atuais enviados após processar ${importResult.analyticRowCount} linhas analíticas de ${importResult.fileCount} arquivo(s)`
                      : `${importResult.rowCount} registros enviados de ${importResult.fileCount} arquivo(s)`}
                  </p>
                </div>
              </div>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
              >
                Nova Importação
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Recebidos</p>
                <p className="text-xl font-bold text-gray-900">{importResult.apiData.total_received}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-xs text-green-700 mb-1">Válidos</p>
                <p className="text-xl font-bold text-green-800">{importResult.apiData.total_valid}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-xs text-blue-700 mb-1">Inseridos</p>
                <p className="text-xl font-bold text-blue-800">{importResult.apiData.inserted}</p>
              </div>
              <div className="bg-teal-50 rounded-lg p-4">
                <p className="text-xs text-teal-700 mb-1">Atualizados</p>
                <p className="text-xl font-bold text-teal-800">{importResult.apiData.updated}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">Registrados</p>
                <p className="text-xl font-bold text-gray-900">{importResult.apiData.logged}</p>
              </div>
              {importResult.apiData.total_invalid > 0 ? (
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-xs text-red-700 mb-1">Inválidos</p>
                  <p className="text-xl font-bold text-red-800">{importResult.apiData.total_invalid}</p>
                </div>
              ) : (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs text-green-700 mb-1">Inválidos</p>
                  <p className="text-xl font-bold text-green-800">0</p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Arquivo</p>
                <p className="font-semibold text-gray-900">{importResult.fileName}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Registros Enviados</p>
                <p className="font-semibold text-gray-900">{importResult.rowCount} linhas</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Data/Hora</p>
                <p className="font-semibold text-gray-900">{importResult.timestamp}</p>
              </div>
            </div>

            {importResult.apiData.total_invalid === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-700" />
                  <p className="text-sm text-green-800">
                    Todos os dados foram processados com sucesso.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-yellow-700" />
                  <p className="text-sm font-semibold text-yellow-800">
                    Upload processado parcialmente - {importResult.apiData.total_invalid} item(ns) inválido(s)
                  </p>
                </div>
              </div>
            )}
          </div>

          {importResult.apiData.invalid_items.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-red-200 overflow-hidden">
              <div className="p-6 border-b border-red-200 bg-red-50">
                <h3 className="text-lg font-bold text-red-900">Itens Inválidos</h3>
                <p className="text-sm text-red-700 mt-1">
                  {importResult.apiData.invalid_items.length} item(ns) não puderam ser processados
                </p>
              </div>
              <div className="divide-y divide-gray-200">
                {importResult.apiData.invalid_items.map((invalid) => (
                  <div key={invalid.index} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-xs font-medium text-gray-500">Linha {invalid.index + 1}</span>
                        <p className="text-sm text-gray-900 font-mono mt-1">
                          EAN: {String(invalid.item.ean || '-')} | CNPJ: {String(invalid.item.cnpj || '-')} | Produto: {String(invalid.item.produto || '-')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {invalid.errors.map((error, errIdx) => (
                        <span
                          key={errIdx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                        >
                          {error}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Dados Enviados</h3>
              <p className="text-sm text-gray-600 mt-1">Mostrando os primeiros registros</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">EAN</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Produto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">CNPJ Loja</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Saldo Inicial</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Entrada</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Saida</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Saldo Final</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Estoque Atual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {importResult.rows.slice(0, 20).map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{row.ean}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.produto}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-mono">{row.cnpjLoja}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.saldoInicial}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.entrada}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.saida}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{row.saldoFinal}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">{row.estoqueAtual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
